import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { FormState } from 'final-form';

import { Forecast, ForecastInfoInput, Queue, StateEnum } from '../../../graphql/components';
import { CreateEditView as Action, CreateEditViewType as ActionType } from '../../../types';
import { toNormalizedDate } from '../../../utils/date';
import { convertFieldsEndOfDateRange, createEndOfDateRange } from '../../../utils/dateRange';
import { getQueue } from '../../../utils/selectors';
import { CreateForecastState, defaultState } from '../config';
import { SKIP_SWITCH_TO_EDIT } from '../constants';

export type FormValues = ReturnType<typeof getFormInitialValues> & {
  id?: string;
  [SKIP_SWITCH_TO_EDIT]?: boolean;
};

export type FormSpyValues = Pick<FormState<FormValues>, 'dirty' | 'valid' | 'values'>;

export const composeForecastInfo = (forecast: Forecast | ForecastInfoInput) => ({
  author: 'Guest', // TODO: update with real user name once auth service is fully working.
  forecastRange: forecast.forecastRange,
  historyRange: forecast.historyRange,
  name: forecast.name,
  notes: '',
  queueId: forecast.queueId,
  concurrentChatsCount: forecast.concurrentChatsCount,
});

export const getInitialState = (
  action: ActionType,
  queues: Queue[],
  initialForecast?: Omit<Forecast, 'timezone'>,
  step: number = defaultState.step
): CreateForecastState => {
  let state = {
    ...defaultState,
    step,
    isCreate: action === Action.CREATE,
    isEdit: action === Action.EDIT,
    isView: action === Action.VIEW,
  };

  if (initialForecast) {
    const queue = getQueue(queues, initialForecast.queueId) as Queue; // Forecast queue is always present in the list of all queues

    state = {
      ...state,
      parameters: {
        ...state.parameters,
        ...composeForecastInfo(initialForecast),
      },
      forecastId: initialForecast.id,
      isProduction: initialForecast.state === StateEnum.Production,
      chartsTimezone: queue.timezone,
      queueTimezone: queue.timezone,
    };
  }

  return state;
};

export const getFormInitialValues = (state: CreateForecastState, initialForecast?: Forecast) => {
  const { isCreate, queueTimezone } = state;

  if (!isCreate && initialForecast && queueTimezone) {
    const { forecastRange, historyRange } = initialForecast;

    return {
      ...initialForecast,
      forecastRange: {
        endDateTime: utcToZonedTime(convertFieldsEndOfDateRange(forecastRange.endDateTime), queueTimezone),
        startDateTime: utcToZonedTime(forecastRange.startDateTime, queueTimezone),
      },
      historyRange: {
        endDateTime: utcToZonedTime(convertFieldsEndOfDateRange(historyRange.endDateTime), queueTimezone),
        startDateTime: utcToZonedTime(historyRange.startDateTime, queueTimezone),
      },
    };
  }

  const { parameters } = state;

  return {
    ...parameters,
    forecastRange: {
      endDateTime: toNormalizedDate(parameters.forecastRange.endDateTime),
      startDateTime: toNormalizedDate(parameters.forecastRange.startDateTime),
    },
    historyRange: {
      endDateTime: toNormalizedDate(parameters.historyRange.endDateTime),
      startDateTime: toNormalizedDate(parameters.historyRange.startDateTime),
    },
  };
};

export const getFirstStepValues = (values: FormValues, timezone: string) => {
  const { concurrentChatsCount, forecastRange, historyRange, name, queueId } = values;

  // Date ranges are always present at this point
  /* eslint-disable @typescript-eslint/no-non-null-assertion */
  return {
    name,
    concurrentChatsCount,
    queueId,
    forecastRange: {
      endDateTime: zonedTimeToUtc(createEndOfDateRange(forecastRange.endDateTime!), timezone).toISOString(),
      startDateTime: zonedTimeToUtc(forecastRange.startDateTime!, timezone).toISOString(),
    },
    historyRange: {
      endDateTime: zonedTimeToUtc(createEndOfDateRange(historyRange.endDateTime!), timezone).toISOString(),
      startDateTime: zonedTimeToUtc(historyRange.startDateTime!, timezone).toISOString(),
    },
  };
  /* eslint-enable @typescript-eslint/no-non-null-assertion */
};
